import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DndContext, DragOverlay, PointerSensor,
  useSensor, useSensors, useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDemo } from "@/store/DemoContext";
import { FORMATIONS, FORMATION_NAMES, FORMATION_COORDS } from "@/data/constants";
import { Pitch } from "@/components/Pitch";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { CountdownBadge } from "@/components/CountdownBadge";
import { playerEligible, isValidXI } from "@/lib/lineup";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { AlertTriangle, Check, Lock, Wand2, Trash2, Save, X } from "lucide-react";

export default function LineupBuilder() {
  const { fixtureId } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { dataset, state, saveDraft, submit, getState, remainingMs, getFixtureById, set } = useDemo();

  const fixture = getFixtureById(fixtureId);
  useEffect(() => { if (fixture) set("activeFixtureId", fixture.id); }, [fixture, set]);

  const fxState = fixture ? getState(fixture) : "OPEN";
  const editable = fxState === "OPEN";
  const squad = useMemo(() => fixture?.homeSquad || [], [fixture]);
  const squadById = useMemo(() => Object.fromEntries(squad.map((p) => [p.id, p])), [squad]);
  const home = fixture ? dataset.clubsById[fixture.homeId] : null;

  const existing = state.submissions[fixtureId] || state.drafts[fixtureId];
  const [formation, setFormation] = useState(existing?.formation || "4-3-3");
  const [assignment, setAssignment] = useState(existing?.assignment || {});
  const [benchOrder, setBenchOrder] = useState(existing?.benchOrder || []);
  const [selected, setSelected] = useState(null); // tap-to-place selected player id
  const [activeId, setActiveId] = useState(null);
  const [sortBy, setSortBy] = useState("number");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // autosave draft
  useEffect(() => {
    if (!editable || !fixture) return;
    if (state.submissions[fixtureId]) return;
    saveDraft(fixtureId, { assignment, formation, benchOrder });
    setSavedFlash(true);
    const to = setTimeout(() => setSavedFlash(false), 1200);
    return () => clearTimeout(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment, formation, benchOrder]);

  // keep bench order in sync with who is unassigned
  useEffect(() => {
    const assignedSet = new Set(Object.values(assignment));
    const remaining = squad.filter((p) => !assignedSet.has(p.id)).map((p) => p.id);
    setBenchOrder((prev) => {
      const kept = prev.filter((id) => remaining.includes(id));
      const added = remaining.filter((id) => !kept.includes(id));
      const next = [...kept, ...added];
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment, squad]);

  const slots = FORMATIONS[formation];
  const coords = FORMATION_COORDS[formation];

  const place = useCallback((pid, targetSlot) => {
    const player = squadById[pid];
    if (!player || !playerEligible(player, targetSlot)) return;
    setAssignment((prev) => {
      const next = { ...prev };
      const fromSlot = Object.keys(next).find((s) => next[s] === pid);
      const occupant = next[targetSlot];
      if (fromSlot && occupant) {
        const occPlayer = squadById[occupant];
        if (playerEligible(occPlayer, fromSlot)) next[fromSlot] = occupant;
        else { delete next[fromSlot]; toast(t("builder.displaced", { name: occPlayer.surname })); }
      } else {
        if (fromSlot) delete next[fromSlot];
        if (occupant) toast(t("builder.displaced", { name: squadById[occupant].surname }));
      }
      next[targetSlot] = pid;
      return next;
    });
    setSelected(null);
  }, [squadById, t]);

  const unassign = useCallback((slot) => {
    setAssignment((prev) => { const n = { ...prev }; delete n[slot]; return n; });
  }, []);

  const onSquadClick = (pid) => {
    if (!editable) return;
    setSelected((s) => (s === pid ? null : pid));
  };
  const onSlotClick = (slot) => {
    if (!editable) return;
    if (selected) { place(selected, slot); return; }
    if (assignment[slot]) setSelected(assignment[slot]); // pick up occupant
  };

  const changeFormation = (f) => {
    const newSlots = FORMATIONS[f];
    const displaced = [];
    const next = {};
    Object.entries(assignment).forEach(([slot, pid]) => {
      if (newSlots.includes(slot)) next[slot] = pid;
      else displaced.push(squadById[pid]?.surname);
    });
    setFormation(f);
    setAssignment(next);
    toast(t("builder.formationSwitched", { formation: f }));
    if (displaced.length) toast(displaced.join(", ") + " → " + t("common.bench"));
  };

  const autoFill = () => {
    // naive, legal-but-suboptimal: fill by shirt number order, never optimal
    const next = {};
    const used = new Set();
    slots.forEach((slot) => {
      const cand = [...squad].sort((a, b) => a.number - b.number)
        .find((p) => !used.has(p.id) && playerEligible(p, slot));
      if (cand) { next[slot] = cand.id; used.add(cand.id); }
    });
    setAssignment(next);
    setSelected(null);
    toast(t("builder.autoFilled"));
  };

  const clearAll = () => { setAssignment({}); setSelected(null); toast(t("builder.cleared")); };

  const valid = isValidXI(assignment, formation);
  const validationMsg = useMemo(() => {
    const count = Object.keys(assignment).filter((s) => assignment[s]).length;
    if (!assignment["GK"]) return t("builder.needGk");
    if (count < 11 || !slots.every((s) => assignment[s])) return t("builder.allSlots");
    return t("builder.ready");
  }, [assignment, slots, t]);

  const doSubmit = () => {
    submit(fixtureId, { assignment, formation, benchOrder, submittedAt: Date.now() });
    setConfirmOpen(false);
    toast.success(t("common.submit") + " ✓");
  };

  const onDragStart = (e) => {
    const id = e.active.id;
    setActiveId(id);
    const pid = id.startsWith("p:") ? id.slice(2) : assignment[id.slice(2)];
    setSelected(null);
    setActiveId(pid);
  };
  const onDragEnd = (e) => {
    const overId = e.over?.id;
    const pid = activeId;
    setActiveId(null);
    if (!overId || !pid) return;
    if (overId === "squad") {
      const fromSlot = Object.keys(assignment).find((s) => assignment[s] === pid);
      if (fromSlot) unassign(fromSlot);
      return;
    }
    if (overId.startsWith("slot:")) place(pid, overId.slice(5));
  };

  const assignedIds = new Set(Object.values(assignment));
  const orderedBench = benchOrder.map((id) => squadById[id]).filter(Boolean);

  const sortedSquad = useMemo(() => {
    const arr = [...squad];
    if (sortBy === "name") arr.sort((a, b) => a.surname.localeCompare(b.surname));
    else if (sortBy === "position") arr.sort((a, b) => a.primary.localeCompare(b.primary));
    else arr.sort((a, b) => a.number - b.number);
    return arr;
  }, [squad, sortBy]);

  if (!fixture || !fixture.homeSquad) {
    return <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">Fixture not available.</div>;
  }

  const activePlayer = activeId ? squadById[activeId] : (selected ? squadById[selected] : null);

  return (
    <TooltipProvider delayDuration={200}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4">
          {/* header */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="font-display text-2xl font-bold uppercase tracking-wide">{home.name}</div>
              <div className="text-xs text-neutral-500">{t("builder.title")}</div>
            </div>
            <div className="flex items-center gap-2">
              {editable
                ? <CountdownBadge ms={remainingMs(fixture)} label={t("builder.lockIn")} testid="builder-countdown" />
                : (fxState === "REVEALED" || fxState === "IN_PLAY" || fxState === "SETTLED") && (
                  <Button data-testid="builder-view-comparison" onClick={() => nav(`/compare/${fixtureId}`)}
                    className="bg-[#3fd7e5] text-black hover:bg-[#2fc3d0]">{t("builder.viewComparison")}</Button>
                )}
            </div>
          </div>

          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
            {/* PITCH */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-display">{t("builder.formationLabel")}</span>
                <Select value={formation} onValueChange={changeFormation} disabled={!editable}>
                  <SelectTrigger data-testid="formation-select" className="w-[130px] h-8 bg-neutral-900 border-neutral-800 font-display">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-800">
                    {FORMATION_NAMES.map((f) => (
                      <SelectItem key={f} value={f} data-testid={`formation-opt-${f}`}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {savedFlash && editable && (
                  <span className="flex items-center gap-1 text-[11px] text-[#b6ff2e] ml-auto"><Save size={12} /> {t("builder.draftSaved")}</span>
                )}
              </div>

              <div className="relative">
                <Pitch>
                  {slots.map((slot) => (
                    <SlotNode key={slot} slot={slot} coord={coords[slot]}
                      player={assignment[slot] ? squadById[assignment[slot]] : null}
                      club={home}
                      eligible={activePlayer ? playerEligible(activePlayer, slot) : null}
                      selected={selected && assignment[slot] === selected}
                      editable={editable}
                      onClick={() => onSlotClick(slot)}
                      onRemove={() => unassign(slot)} />
                  ))}
                </Pitch>

                {!editable && (
                  <div data-testid="locked-overlay" className="absolute inset-0 rounded-[10px] bg-neutral-950/55 backdrop-blur-[1px] grid place-items-center">
                    <div className="flex flex-col items-center gap-2 text-neutral-200">
                      <Lock size={26} />
                      <span className="font-display uppercase tracking-widest text-sm">{t("builder.lockedOverlay")}</span>
                    </div>
                  </div>
                )}
              </div>

              {editable && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button data-testid="auto-fill-btn" variant="outline" size="sm" onClick={autoFill}
                    className="border-neutral-700 gap-1.5"><Wand2 size={14} /> {t("builder.autoFill")}</Button>
                  <Button data-testid="clear-all-btn" variant="outline" size="sm" onClick={clearAll}
                    className="border-neutral-700 gap-1.5"><Trash2 size={14} /> {t("builder.clearAll")}</Button>
                  {selected && <span className="text-[11px] text-[#b6ff2e] flex items-center gap-1">{t("builder.tapToPlace")}</span>}
                </div>
              )}
            </div>

            {/* SQUAD */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-display">{t("builder.squadTitle")}</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="squad-sort" className="w-[130px] h-8 bg-neutral-900 border-neutral-800 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-900 border-neutral-800">
                    <SelectItem value="number">{t("builder.byNumber")}</SelectItem>
                    <SelectItem value="name">{t("builder.byName")}</SelectItem>
                    <SelectItem value="position">{t("builder.byPosition")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <SquadDrop>
                <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1" data-testid="squad-list">
                  {sortedSquad.map((p) => (
                    <SquadRow key={p.id} player={p} club={home}
                      assigned={assignedIds.has(p.id)}
                      selected={selected === p.id}
                      editable={editable}
                      onClick={() => onSquadClick(p.id)} />
                  ))}
                </div>
              </SquadDrop>

              {/* bench */}
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-display mb-1">{t("builder.benchTitle")}</div>
                <div className="text-[11px] text-neutral-600 mb-1.5">{t("builder.benchHint")}</div>
                <BenchList items={orderedBench} editable={editable} onReorder={(ids) => setBenchOrder(ids)} />
              </div>
            </div>
          </div>

          {/* submit bar */}
          {editable && (
            <div className="sticky bottom-0 mt-5 -mx-3 sm:-mx-6 px-3 sm:px-6 py-3 bg-neutral-950/90 backdrop-blur border-t border-neutral-800 flex items-center justify-between gap-3">
              <span className={`text-xs flex items-center gap-1.5 ${valid ? "text-[#b6ff2e]" : "text-neutral-400"}`} data-testid="validation-msg">
                {valid ? <Check size={14} /> : <AlertTriangle size={14} />} {validationMsg}
              </span>
              <Button data-testid="submit-xi-btn" disabled={!valid} onClick={() => setConfirmOpen(true)}
                className="bg-[#b6ff2e] text-black hover:bg-[#a2e626] font-display uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed">
                {state.submissions[fixtureId] ? t("common.save") : t("builder.submitXI")}
              </Button>
            </div>
          )}
        </div>

        <DragOverlay>
          {activeId && squadById[activeId] ? (
            <div className="drag-ghost rounded-lg bg-neutral-800 border border-[#b6ff2e] px-2 py-1 flex items-center gap-1.5">
              <PlayerAvatar player={squadById[activeId]} club={home} size={22} />
              <span className="text-xs font-medium">{squadById[activeId].surname}</span>
            </div>
          ) : null}
        </DragOverlay>

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent data-testid="submit-confirm-modal" className="bg-neutral-950 border-neutral-800">
            <DialogHeader>
              <DialogTitle className="font-display uppercase">{t("builder.confirmTitle")}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-neutral-400">{t("builder.confirmBody")}</p>
            <DialogFooter>
              <Button variant="outline" className="border-neutral-700" onClick={() => setConfirmOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="submit-confirm-btn" className="bg-[#b6ff2e] text-black hover:bg-[#a2e626]" onClick={doSubmit}>{t("common.confirm")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DndContext>
    </TooltipProvider>
  );
}

function SlotNode({ slot, coord, player, club, eligible, selected, editable, onClick, onRemove }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot:${slot}` });
  const drag = useDraggable({ id: `s:${slot}`, disabled: !player || !editable });
  const cls = eligible === true ? "slot-eligible border-[#b6ff2e]"
    : eligible === false ? "slot-ineligible border-neutral-700"
      : "border-neutral-600";
  return (
    <div ref={setNodeRef} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${coord.x}%`, top: `${coord.y}%` }}>
      <button
        ref={drag.setNodeRef} {...(player && editable ? drag.listeners : {})} {...(player && editable ? drag.attributes : {})}
        data-testid={`slot-${slot}`}
        onClick={onClick}
        aria-label={player ? `${slot}: ${player.name}` : `${slot} empty slot`}
        className={`slot-node relative grid place-items-center rounded-full border-2 transition-all ${cls} ${
          player ? "bg-neutral-900" : "bg-neutral-900/40"
        } ${selected ? "ring-2 ring-[#b6ff2e]" : ""} ${isOver ? "scale-110" : ""}`}
        style={{ width: 54, height: 54 }}>
        {player ? (
          <>
            <PlayerAvatar player={player} club={club} size={34} />
            <span className="absolute -bottom-4 whitespace-nowrap text-[9px] font-display font-semibold text-neutral-200 bg-neutral-950/70 px-1 rounded">
              {player.number} {player.surname}
            </span>
            {player.availabilityProbability < 1 && (
              <span className="absolute -top-1 -right-1 text-amber-400"><AlertTriangle size={12} /></span>
            )}
          </>
        ) : (
          <span className="text-[9px] font-display font-bold text-neutral-500">{slot}</span>
        )}
      </button>
      {player && editable && (
        <button data-testid={`slot-remove-${slot}`} onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1.5 -left-1.5 w-4 h-4 rounded-full bg-neutral-800 border border-neutral-600 grid place-items-center text-neutral-400 hover:text-red-400">
          <X size={9} />
        </button>
      )}
    </div>
  );
}

function SquadDrop({ children }) {
  const { setNodeRef, isOver } = useDroppable({ id: "squad" });
  return <div ref={setNodeRef} className={isOver ? "rounded-lg ring-1 ring-[#b6ff2e]/40" : ""}>{children}</div>;
}

function BenchList({ items, onReorder, editable }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = items.map((p) => p.id);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragEnd={(e) => {
        const { active, over } = e;
        if (over && active.id !== over.id) {
          onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
        }
      }}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="flex flex-wrap gap-1.5" data-testid="bench-list">
          {items.map((p, i) => (<BenchChip key={p.id} player={p} index={i} editable={editable} />))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function BenchChip({ player, index, editable }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: player.id, disabled: !editable });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isFirst7 = index < 7;
  return (
    <span ref={setNodeRef} style={style} {...attributes} {...listeners}
      data-testid={`bench-chip-${player.id}`}
      className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1.5 select-none ${
        isFirst7 ? "bg-neutral-900 border-neutral-700 text-neutral-300" : "bg-neutral-950 border-neutral-800 text-neutral-600"
      } ${editable ? "cursor-grab active:cursor-grabbing" : ""}`}>
      <span className="font-display font-bold text-neutral-500 tabular w-3">{index + 1}</span>
      <span className="tabular text-neutral-500">{player.number}</span> {player.surname}
    </span>
  );
}

function SquadRow({ player, club, assigned, selected, editable, onClick }) {
  const { t } = useTranslation();
  const drag = useDraggable({ id: `p:${player.id}`, disabled: !editable || assigned });
  return (
    <div
      ref={drag.setNodeRef} {...(editable && !assigned ? drag.listeners : {})} {...(editable && !assigned ? drag.attributes : {})}
      data-testid={`squad-row-${player.id}`}
      onClick={onClick}
      role="button" tabIndex={editable ? 0 : -1}
      onKeyDown={(e) => { if (e.key === "Enter" && editable) onClick(); }}
      aria-label={`${player.name}, ${player.primary}, ${player.eligible.join(", ")}`}
      className={`flex items-center gap-3 px-2.5 py-2 rounded-lg border transition-colors cursor-pointer ${
        selected ? "border-[#b6ff2e] bg-[#b6ff2e]/10"
          : assigned ? "border-neutral-900 bg-neutral-950 opacity-45"
            : "border-neutral-800 bg-neutral-900/50 hover:border-neutral-600"
      }`}>
      <span className="w-6 text-center font-display font-bold text-neutral-400 tabular text-sm">{player.number}</span>
      <PlayerAvatar player={player} club={club} size={30} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{player.name}</span>
          {player.availabilityProbability < 1 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span data-testid={`doubtful-${player.id}`} className="text-amber-400 shrink-0"><AlertTriangle size={13} /></span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px] bg-neutral-900 border-neutral-700 text-xs">{t("builder.doubtfulTip")}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex gap-1 mt-0.5 flex-wrap">
          {player.eligible.map((pos) => (
            <span key={pos} className={`text-[9px] px-1.5 py-0.5 rounded font-display ${pos === player.primary ? "bg-neutral-700 text-neutral-100" : "bg-neutral-800/70 text-neutral-500"}`}>{pos}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
